import type {
  TrimMap,
  TrimMapTableProps
} from "@dealer-center/inventory-pages-gvm-interface";
import type {
  InterstateOnChangeEvent,
  SelectInputEventValue,
  TypeSafeTableColumns
} from "@dealer-center/core-components";
import {
  Button,
  Table,
  TextInput,
  useToaster
} from "@dealer-center/core-components";
import { useState } from "react";
import { useIsGvmRwAccessUser } from "../util/gvmUserUtil";

const defaultTrimMap: TrimMap = {
  recordId: "",
  thirdPartyId: "",
  globalVehicleTrimId: "",
  thirdPartyVehicleTrimId: "",
  manuallyCreated: true,
  active: true
};

export const TrimMapTable = ({
  trimMapList,
  isLoading,
  handleAdd,
  handleDelete
}: TrimMapTableProps) => {
  const [trimMap, setTrimMap] = useState<TrimMap>(defaultTrimMap);
  const isGvmRwAccessUser = useIsGvmRwAccessUser();
  const isElementDisabled = !isGvmRwAccessUser || isLoading;
  const { toastError } = useToaster();

  const handleInputChange = (
    event: InterstateOnChangeEvent<SelectInputEventValue>
  ) => {
    const name = event.target.name || "";
    const value = event.target.value;

    setTrimMap({ ...trimMap, [name]: value });
  };

  function validateTrimMap(): boolean {
    return (
      !!trimMap.thirdPartyId &&
      !!trimMap.thirdPartyVehicleTrimId &&
      !trimMapList.some(
        item =>
          item.thirdPartyId.toLowerCase() ===
            trimMap.thirdPartyId.toLowerCase() &&
          item.thirdPartyVehicleTrimId.toLowerCase() ===
            trimMap.thirdPartyVehicleTrimId.toLowerCase()
      )
    );
  }

  const handleMapAdd = () => {
    if (validateTrimMap()) {
      handleAdd(trimMap);
      setTrimMap(defaultTrimMap);
    } else {
      toastError(`Mapping already exists`);
    }
  };

  function getTrimColumns(
    onDelete: (record: TrimMap) => void
  ): TypeSafeTableColumns<TrimMap> {
    return [
      {
        key: "thirdPartyId",
        dataIndex: "thirdPartyId",
        title: "Third Party ID"
      },
      {
        key: "thirdPartyVehicleTrimId",
        dataIndex: "thirdPartyVehicleTrimId",
        title: "Third Party Vehicle Trim ID"
      },
      {
        key: "manuallyCreated",
        dataIndex: "manuallyCreated",
        title: "Manually Created",
        render: (value: boolean) => (value ? "Yes" : "No")
      },
      {
        key: "action",
        title: "Action",
        render: (value, record: TrimMap) =>
          record.manuallyCreated &&
          !isElementDisabled && (
            <span
              style={{
                cursor: "pointer",
                color: "blue"
              }}
              title="Delete"
              onClick={() => onDelete(record)}
            >
              Delete
            </span>
          )
      }
    ];
  }

  return (
    <>
      <div style={{ width: "99%" }}>
        <Table.Table
          columns={getTrimColumns(handleDelete)}
          data={trimMapList}
          data-testid="gvm-trim-map-results"
          dataDensity="small"
          emptyText="No mapping available"
          enablePagination={false}
          isLoading={isLoading}
          scrollY={500}
          sortableColumns={true}
          tableLayout="auto"
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "10px"
        }}
      >
        <div style={{ flex: "1", marginRight: "10px" }}>
          <TextInput
            data-testid="gvm-trim-map-third-party-id"
            disabled={isElementDisabled}
            name="thirdPartyId"
            placeholder="Third Party Id"
            type="text"
            value={trimMap.thirdPartyId}
            onChange={handleInputChange}
          />
        </div>
        <div style={{ flex: "1", marginRight: "10px" }}>
          <TextInput
            data-testid="gvm-trim-map-third-party-trim-id"
            disabled={isElementDisabled}
            name="thirdPartyVehicleTrimId"
            placeholder="Third Party Trim Id"
            type="text"
            value={trimMap.thirdPartyVehicleTrimId}
            onChange={handleInputChange}
          />
        </div>

        <Button
          analyticsConfiguration={{
            eventName: "Add Trim Map button clicked"
          }}
          buttonStyle="secondary"
          data-testid="gvm-trim-map-third-party-add-button"
          disabled={
            isElementDisabled ||
            !trimMap.thirdPartyId ||
            !trimMap.thirdPartyVehicleTrimId
          }
          type="button"
          onClick={handleMapAdd}
        >
          Add Trim Map
        </Button>
      </div>
    </>
  );
};
